import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';

export default function BuildingRubricBuilder({ tenantId }) {
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  
  const [config, setConfig] = useState({
    frequency: 'Sprint',
    // NEW DYNAMIC SCHEMA
    customScale: [
      { value: 1, label: '1 - Needs Support' },
      { value: 2, label: '2 - Target' },
      { value: 3, label: '3 - Exceeds' }
    ],
    metrics: ['On Task', 'Shows Respect', 'Completes Work']
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // 1. Fetch available buildings
  useEffect(() => {
    const fetchBuildings = async () => {
      if (!tenantId) return;
      try {
        const usersRef = collection(db, `districts/${tenantId}/users`);
        const usersSnap = await getDocs(usersRef);
        const uniqueBuildings = new Set();
        
        usersSnap.forEach(doc => {
          const data = doc.data();
          if (data.schoolId && data.schoolId !== 'District Office') {
            uniqueBuildings.add(data.schoolId);
          }
        });
        
        const sortedBuildings = Array.from(uniqueBuildings).sort();
        setBuildings(sortedBuildings);
        if (sortedBuildings.length > 0) setSelectedBuilding(sortedBuildings[0]);
      } catch (error) {
        console.error("Error fetching buildings:", error);
      }
    };
    fetchBuildings();
  }, [tenantId]);

  // 2. Fetch Config
  useEffect(() => {
    const fetchConfig = async () => {
      if (!tenantId || !selectedBuilding) return;
      try {
        const configRef = doc(db, `districts/${tenantId}/settings`, `rubric_${selectedBuilding}`);
        const configSnap = await getDoc(configRef);
        
        if (configSnap.exists()) {
          const data = configSnap.data();
          // Fallback just in case old data structure leaks in during testing
          if (!data.customScale) {
            data.customScale = [
              { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
            ];
          }
          setConfig(data);
        } else {
          setConfig({
            frequency: 'Sprint',
            customScale: [
              { value: 1, label: '1 - Needs Support' },
              { value: 2, label: '2 - Target' },
              { value: 3, label: '3 - Exceeds' }
            ],
            metrics: ['On Task', 'Shows Respect', 'Completes Work']
          });
        }
      } catch (error) {
        console.error("Error fetching building config:", error);
      }
    };
    fetchConfig();
  }, [tenantId, selectedBuilding]);

  // Metrics Handlers
  const handleAddMetric = () => setConfig({ ...config, metrics: [...config.metrics, 'New Metric'] });
  const handleUpdateMetric = (index, newValue) => {
    const updated = [...config.metrics];
    updated[index] = newValue;
    setConfig({ ...config, metrics: updated });
  };
  const handleRemoveMetric = (index) => {
    const updated = config.metrics.filter((_, i) => i !== index);
    setConfig({ ...config, metrics: updated });
  };

  // NEW: Custom Scale Handlers
  const handleAddScalePoint = () => {
    // Automatically suggest the next logical number
    const maxVal = config.customScale.length > 0 ? Math.max(...config.customScale.map(s => s.value)) : 0;
    setConfig({ ...config, customScale: [...config.customScale, { value: maxVal + 1, label: 'New Label' }] });
  };
  
  const handleUpdateScalePoint = (index, field, newValue) => {
    const updated = [...config.customScale];
    updated[index][field] = field === 'value' ? Number(newValue) : newValue;
    setConfig({ ...config, customScale: updated });
  };

  const handleRemoveScalePoint = (index) => {
    const updated = config.customScale.filter((_, i) => i !== index);
    setConfig({ ...config, customScale: updated });
  };

  // 3. Save
  const handleSaveConfig = async () => {
    if (!tenantId || !selectedBuilding) return;
    setIsSaving(true);
    setSaveMessage('Saving configuration...');
    
    try {
      // Sort the custom scale by value so it displays perfectly left-to-right on the teacher portal
      const sortedConfig = {
        ...config,
        customScale: [...config.customScale].sort((a, b) => a.value - b.value)
      };

      const configRef = doc(db, `districts/${tenantId}/settings`, `rubric_${selectedBuilding}`);
      await setDoc(configRef, sortedConfig);
      
      setConfig(sortedConfig);
      setSaveMessage('Building Rubric Saved!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error("Error saving config:", error);
      setSaveMessage('Error saving configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxWidth: '700px', fontFamily: 'sans-serif' }}>
      
      <div style={{ borderBottom: '2px solid #003366', paddingBottom: '15px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ margin: 0, color: '#003366', fontSize: '24px' }}>Building Rubric Engine</h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>Customize GenEd behavior tracking per school.</p>
        </div>
        <button 
          onClick={handleSaveConfig} 
          disabled={isSaving} 
          style={{ padding: '10px 20px', backgroundColor: '#ba0c2f', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: isSaving ? 'not-allowed' : 'pointer' }}
        >
          {isSaving ? 'Saving...' : '💾 Save Building Rules'}
        </button>
      </div>

      {/* Select Building */}
      <div style={{ marginBottom: '25px', backgroundColor: '#f0f5fa', padding: '15px', borderRadius: '6px', border: '1px solid #cce0ff' }}>
        <label style={{ display: 'block', fontWeight: 'bold', color: '#003366', marginBottom: '8px' }}>Target Building</label>
        <select 
          value={selectedBuilding} 
          onChange={(e) => setSelectedBuilding(e.target.value)} 
          style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #003366', fontSize: '15px', fontWeight: 'bold' }}
        >
          {buildings.length === 0 ? <option>Loading buildings...</option> : null}
          {buildings.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {saveMessage && <span style={{ display: 'block', marginTop: '10px', fontWeight: 'bold', color: saveMessage.includes('Error') ? '#ba0c2f' : '#28a745' }}>{saveMessage}</span>}
      </div>

      <div style={{ marginBottom: '30px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '8px', fontSize: '14px' }}>Reporting Frequency</label>
        <select 
          value={config.frequency} 
          onChange={(e) => setConfig({...config, frequency: e.target.value})} 
          style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value="Sprint">Once per Sprint (Bi-Weekly)</option>
          <option value="Weekly">Weekly (Every Friday)</option>
          <option value="Daily">Daily (Every afternoon)</option>
        </select>
      </div>

      {/* NEW: DYNAMIC SCALE BUILDER */}
      <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #eee', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <span>Custom Scoring Scale</span>
          <button 
            onClick={handleAddScalePoint} 
            style={{ background: 'none', border: 'none', color: '#0056b3', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
          >
            + Add Option
          </button>
        </h3>
        <p style={{ margin: '0 0 15px 0', fontSize: '12px', color: '#666' }}>Define the point value and the text/emoji label the teacher will click.</p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {config.customScale.map((scalePoint, index) => (
            <div key={index} style={{ display: 'flex', gap: '10px' }}>
              <input 
                type="number" 
                value={scalePoint.value} 
                onChange={(e) => handleUpdateScalePoint(index, 'value', e.target.value)} 
                style={{ width: '80px', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }} 
                placeholder="Pts"
              />
              <input 
                type="text" 
                value={scalePoint.label} 
                onChange={(e) => handleUpdateScalePoint(index, 'label', e.target.value)} 
                style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }} 
                placeholder="Label (e.g. 'Target' or '🙂')"
              />
              <button 
                onClick={() => handleRemoveScalePoint(index)} 
                style={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '0 15px', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold' }}
              >
                X
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Define Metrics */}
      <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #eee' }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <span>Measured Behaviors</span>
          <button 
            onClick={handleAddMetric} 
            style={{ background: 'none', border: 'none', color: '#0056b3', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
          >
            + Add Metric
          </button>
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {config.metrics.map((metric, index) => (
            <div key={index} style={{ display: 'flex', gap: '10px' }}>
              <input 
                type="text" 
                value={metric} 
                onChange={(e) => handleUpdateMetric(index, e.target.value)} 
                style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }} 
              />
              <button 
                onClick={() => handleRemoveMetric(index)} 
                style={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '0 15px', color: '#ba0c2f', cursor: 'pointer', fontWeight: 'bold' }}
              >
                X
              </button>
            </div>
          ))}
        </div>
      </div>
      
    </div>
  );
}